import Big from 'big.js';
import {gte, numUtil} from './num';
import {calcValueRv} from '../calc';

export const LeverageDefault = {unit: 'X', precision: 2};

export const leverageUtil = {
  leverageErToLeverage(leverageEr: number, leverageScale: number = 8, {removeEnd0 = false, unit = LeverageDefault.unit}: {removeEnd0?: boolean; unit?: string} = {}): string {
    const leverage = numUtil.ceil(leverageEr, LeverageDefault.precision, leverageScale);
    return (removeEnd0 ? numUtil.trimEnd0(leverage) : leverage) + unit;
  },
  getMaxLeverageByLeverageOptions(leverageOptions: LeverageEntity[]): LeverageEntity {
    const maxLeverage = leverageOptions.reduce((p, c) => (Big(p.leverageEr).gt(c.leverageEr) ? p : c), {leverageEr: 0});
    return {
      ...maxLeverage,
      leverageX: `${maxLeverage.leverage}X`,
    };
  },
  convertSlideOptions(options: LeverageEntity[]) {
    return options
      .sort((a, b) => a.leverageEr - b.leverageEr)
      .map((l) => ({
        value: l.leverageEr,
        label: `${l.leverage}X`,
      }));
  },
  ceilLeverageEr(leverageEr, precision, scale) {
    return Big(numUtil.ceil(leverageEr, precision, scale)).times(Big(10).pow(scale)).toNumber();
  },
};

function convertLeverageOptions(initialMarginEr: number, leverages: Leverage[]) {
  const leverageData = leverages.find((o) => o.initialMarginEr === initialMarginEr);
  return leverageData ? leverageData.options : null;
}

/**
 * 获取杠杆options
 * 条件 1. 根据合约leverageMargin[例如: lm_index_12]和仓位当前价值匹配leverage margin item
 * 条件 2. 使用系统最大杠杆矫正杠杆最大option
 * */
export function genLeverageOptions(contract: ContractPair, position: Position, leverageMarginsMap: Map<number, LeverageMargins>): LeverageEntity[] {
  const fullOptions = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 17, 20, 21, 22, 23, 25, 29, 34, 40, 50, 67, 100];

  if (!contract) {
    return [];
  }

  const {leverageMargin, maxLeverage: sysMaxLeverage} = contract;
  if (!position || !leverageMarginsMap.has(leverageMargin)) {
    return [];
  }

  const posValue = genPosValue(position);
  const posValue2 = Big(posValue).div(contract.valueFactor).toNumber();
  const marginItems = genMarginItems(position, leverageMargin, leverageMarginsMap);
  const items = marginItems.sort((a, b) => a.notionalValueRv - b.notionalValueRv).find(({notionalValueRv}) => notionalValueRv >= posValue2);
  const matchedMaxLeverage = Math.min(items?.maxLeverage || 0, sysMaxLeverage);
  const pickedValues = (pickValues(fullOptions, matchedMaxLeverage) || []).sort((a, b) => a - b);
  return pickedValues.map((leverage) => ({leverage, leverageEr: leverage * contract.ratioFactor, leverageX: `${leverage}X`}));
}

export function genNotionalValueNumber(contract: ContractPair, position: Position, leverage: number, leverageMarginsMap: Map<number, LeverageMargins>) {
  if (!contract) {
    return '0';
  }
  const {leverageMargin} = contract;
  if (!leverageMarginsMap.has(leverageMargin)) {
    return '0';
  }
  const marginItems = genMarginItems(position, leverageMargin, leverageMarginsMap);

  const item = marginItems.sort((a, b) => a.maxLeverage - b.maxLeverage).find(({maxLeverage}) => maxLeverage >= leverage);

  if (!item) {
    const notionalValues = marginItems.map(({notionalValueRv}) => notionalValueRv);
    return Math.min(...notionalValues);
  }

  return item?.notionalValueRv || 0;
}

export function genNotionalValue(contract: ContractPair, position: Position, leverageEr: number, leverageMarginsMap: Map<number, LeverageMargins>) {
  const leverage = Big(leverageEr).div(contract.ratioFactor).toNumber();
  return numUtil.delimit(genNotionalValueNumber(contract, position, leverage, leverageMarginsMap));
}

/**
 * 查询动态动态 RiskLimit 档位
 * @param contract
 * @param position
 * @param leverageMarginsMap
 * @param notionValue 默认是查询目前的档位，notionValue 支持按照这个值查询档位
 */
export function getMaintenanceMargin(contract: ContractPair, position: Position, leverageMarginsMap: Map<number, LeverageMargins>, notionValue?: number) {
  if (!contract) {
    return initMaintenance();
  }

  const {leverageMargin} = contract;
  if (!leverageMarginsMap.has(leverageMargin)) {
    return initMaintenance();
  }

  const posValue = notionValue || genPosValue(position);
  const posValue2 = Big(posValue).div(contract.valueFactor).toNumber();
  const marginItems = genMarginItems(position, leverageMargin, leverageMarginsMap).sort((a, b) => a.notionalValueRv - b.notionalValueRv);
  const item = marginItems.find(({notionalValueRv}) => notionalValueRv >= posValue2) || marginItems[marginItems.length - 1];
  if (!item) {
    return initMaintenance();
  }

  const {notionalValueRv, maintenanceMarginRateRr, maintenanceAmountRv} = item;
  const maintenanceMargin = Number(Big(notionalValueRv).times(maintenanceMarginRateRr).minus(maintenanceAmountRv));
  return {maintenanceAmountRv: Number(maintenanceAmountRv), maintenanceMargin, maintenanceMarginRateRr: Number(maintenanceMarginRateRr)};
}

export function calcMM(contract, position, marginCache) {
  const {leverageMargin} = contract;
  if (!marginCache.has(leverageMargin) || !position) {
    return initMaintenance();
  }

  const {size, markPriceEp} = position;
  const posValue = calcValueRv(contract.contractSide, contract.contractSize, contract.priceFactor, size, markPriceEp);
  const marginItems = genMarginItems(position, leverageMargin, marginCache);
  const item = marginItems.sort((a, b) => a.notionalValueRv - b.notionalValueRv).find(({notionalValueRv}) => gte(notionalValueRv, posValue));
  if (!item) {
    return initMaintenance();
  }

  const {maintenanceMarginRateRr, maintenanceAmountRv} = item;
  const maintenanceMargin = Number(Big(posValue).times(maintenanceMarginRateRr).minus(maintenanceAmountRv));
  return {maintenanceAmountRv: Number(maintenanceAmountRv), maintenanceMargin, maintenanceMarginRateRr: Number(maintenanceMarginRateRr)};
}

function pickValues(items: number[], maxLeverage: number): number[] {
  const num = 8;
  const array = items.filter((item) => item <= maxLeverage);
  if (array.length <= num) {
    return array;
  }
  let result = [];

  const min = array.reduce((min, item) => (item < min ? item : min), Infinity);
  result.push(min);

  const max = array.reduce((max, item) => (item > max ? item : max), -Infinity);
  result.push(max);

  let remaining = num - 2;
  const len = array.length;
  const interval = Math.floor((len - 2) / remaining);

  for (let i = 1; i < len - 1; i += interval) {
    result.push(array[i]);
    remaining--;
    if (remaining === 0) {
      break;
    }
  }
  return result;
}

function initMaintenance() {
  return {maintenanceAmountRv: 0, maintenanceMargin: 0, maintenanceMarginRateRr: 0};
}

function genPosValue(position: Position) {
  if (!position) {
    return 0;
  }
  const {value, buyLeavesValueRv, sellLeavesValueRv} = position;

  const maxLeaveValue = Math.max(Number(buyLeavesValueRv) || 0, Number(sellLeavesValueRv) || 0);
  return Big(value).plus(maxLeaveValue).toNumber();
}

// http://confluence.cmexpro.com/pages/viewpage.action?pageId=75268689
function genMarginItems(position = {size: 0, riskLimitIndexId: 0}, leverageMargin, marginsMap: Map<number, LeverageMargins>) {
  const {size, riskLimitIndexId} = position;
  if (size == 0) {
    return marginsMap.get(leverageMargin)?.items || [];
  } else {
    if (riskLimitIndexId == 0) {
      const margin = marginsMap.get(leverageMargin) || {index_id: 0};
      const lastIndexId = Number(margin.index_id) - 1000;
      return (marginsMap.get(lastIndexId) || marginsMap.get(leverageMargin))?.items || [];
    } else {
      return (marginsMap.get(riskLimitIndexId) || marginsMap.get(leverageMargin))?.items;
    }
  }
}
